<script setup>
import { defineModel, ref, watch, defineProps, defineEmits, onUnmounted } from 'vue';
import { createToaster } from '@meforma/vue-toaster';
import TrashCanSvg from '@/components/UI/svg/TrashCanSvg.vue';
import PlusCircleSvg from '@/components/UI/svg/PlusCircleSvg.vue';
import ToolTip from '@/components/UI/ToolTip.vue';
import BitrixAccountLinkGuideButton from '@/components/CreateConnectionComponents/BitrixUpload/components/BitrixAccountLinkGuideButton.vue';
import BitrixAccountAttachForm from '@/components/CreateConnectionComponents/BitrixUpload/components/BitrixAccountAttachForm.vue';
import { Api } from '@/service/api';

const isVisible = defineModel();
const props = defineProps({
    account: {
        type: String,
        default: null,
    },
});
const emit = defineEmits(['update:account']);
const toaster = createToaster();

const modalView = ref('list');
const accounts = ref([]);
const isLoading = ref(false);
const pendingAttachDomain = ref(null);

let checkInterval = null;

watch(isVisible, (visible) => {
    if (visible) {
        loadAccounts();
    } else {
        modalView.value = 'list';
        pendingAttachDomain.value = null;
        stopAccountCheck();
    }
});

function closeModal() {
    isVisible.value = false;
}

function selectAccount(selectedAccount) {
    emit('update:account', selectedAccount.domain);
    closeModal();
}

function openAttachView() {
    modalView.value = 'attach';
}

function backToList() {
    modalView.value = 'list';
}

async function loadAccounts() {
    try {
        isLoading.value = true;
        accounts.value = await Api.getUserBitrixAccounts();
    } catch (error) {
        console.error('Ошибка загрузки аккаунтов Bitrix:', error);
        toaster.error('Не удалось загрузить аккаунты Bitrix');
    } finally {
        isLoading.value = false;
    }
}

function startAccountCheck() {
    if (checkInterval) {
        clearInterval(checkInterval);
    }

    checkInterval = setInterval(async () => {
        try {
            const previousIds = new Set(accounts.value.map((item) => item.id));
            const newAccounts = await Api.getUserBitrixAccounts();

            if (newAccounts.length !== accounts.value.length) {
                accounts.value = newAccounts;

                const addedAccount = newAccounts.find((item) => !previousIds.has(item.id));
                if (addedAccount) {
                    if (
                        !pendingAttachDomain.value
                        || addedAccount.domain === pendingAttachDomain.value
                    ) {
                        emit('update:account', addedAccount.domain);
                        toaster.success('Аккаунт Bitrix успешно привязан');
                        modalView.value = 'list';
                        pendingAttachDomain.value = null;
                    }
                    stopAccountCheck();
                }
            }
        } catch (error) {
            console.error('Ошибка проверки аккаунтов Bitrix:', error);
        }
    }, 2000);
}

function stopAccountCheck() {
    if (checkInterval) {
        clearInterval(checkInterval);
        checkInterval = null;
    }
}

function handleOauthStarted(portalDomain) {
    pendingAttachDomain.value = portalDomain;
    startAccountCheck();
}

async function deleteAccount(accountToDelete) {
    try {
        await Api.removeBitrixAccount(accountToDelete.id);
        accounts.value = accounts.value.filter((item) => item.id !== accountToDelete.id);

        if (props.account === accountToDelete.domain) {
            emit('update:account', null);
        }

        toaster.success(`Аккаунт «${accountToDelete.domain}» удалён`);
    } catch (error) {
        console.error('Ошибка удаления аккаунта Bitrix:', error);
        toaster.error('Не удалось удалить аккаунт Bitrix');
    }
}

function handleOverlayClick() {
    if (modalView.value === 'list') {
        closeModal();
    }
}

onUnmounted(() => {
    stopAccountCheck();
});
</script>

<template>
    <Teleport to="body">
        <Transition name="modal-fade">
            <div
                v-if="isVisible"
                class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-[2px]"
                @click.self="handleOverlayClick"
            >
                <div
                    class="w-full max-w-md md:max-w-lg bg-white rounded-md shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-gray-100"
                    role="dialog"
                    aria-modal="true"
                    :aria-labelledby="modalView === 'list' ? 'bitrix-account-modal-title' : 'bitrix-account-attach-title'"
                >
                    <BitrixAccountAttachForm
                        v-if="modalView === 'attach'"
                        @back="backToList"
                        @oauth-started="handleOauthStarted"
                    />

                    <template v-else>
                        <div class="flex items-start justify-between gap-3 px-6 pt-6 pb-4 border-b border-gray-100">
                            <div class="flex-1 min-w-0 pr-2">
                                <h2 id="bitrix-account-modal-title" class="text-lg font-semibold text-gray-900">
                                    Выбор аккаунта Bitrix
                                </h2>
                                <p class="text-sm text-gray-500 mt-1">
                                    Выберите аккаунт или привяжите новый
                                </p>
                            </div>

                            <div class="flex items-start gap-2 flex-shrink-0">
                                <BitrixAccountLinkGuideButton />

                                <button
                                    type="button"
                                    class="flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                                    aria-label="Закрыть"
                                    @click="closeModal"
                                >
                                    <svg class="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div class="px-6 py-5 min-h-[120px] flex flex-col">
                            <p v-if="isLoading" class="text-sm text-gray-500 py-3">
                                Загрузка аккаунтов...
                            </p>

                            <div
                                v-for="acc in accounts"
                                :key="acc.id"
                                role="button"
                                tabindex="0"
                                class="py-3 px-4 my-2 border-2 rounded-md flex items-center gap-3 cursor-pointer transition-colors duration-150"
                                :class="account === acc.domain
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-blue-200 hover:bg-blue-50/60'"
                                @click="selectAccount(acc)"
                                @keyup.enter="selectAccount(acc)"
                            >
                                <span class="flex-1 truncate">{{ acc.domain }}</span>
                                <ToolTip text="Удалить аккаунт" position="bottom">
                                    <button
                                        type="button"
                                        class="flex-shrink-0 size-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                                        aria-label="Удалить аккаунт"
                                        @click.stop="deleteAccount(acc)"
                                    >
                                        <TrashCanSvg class="size-5" />
                                    </button>
                                </ToolTip>
                            </div>

                            <button
                                type="button"
                                class="flex items-center gap-3 py-3 px-4 my-2 border-2 border-dashed rounded-md border-blue-200 text-blue-600 hover:bg-blue-50 transition-colors duration-150 w-full group"
                                @click="openAttachView"
                            >
                                <PlusCircleSvg class="size-6 stroke-blue-500 group-hover:stroke-blue-600 transition-colors duration-150" />
                                <span class="font-semibold text-sm">
                                    Привязать аккаунт
                                </span>
                            </button>
                        </div>
                    </template>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
    transition: opacity 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
    opacity: 0;
}
</style>
